// @ts-nocheck
import React from 'react';

import { withApollo } from '@apollo/client/react/hoc';
import compose from 'lodash/flowRight';

import { colors } from '@crehana/ui';

import { NewArrowRight } from '@crehana/icons';

import { Container, Col, Loader, SpacingType } from '@crehana/ui';

import { useTranslation } from '@crehana/i18n';

import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { useProductsContainer } from '@crehana/web/dist/ReactContexts/Products';

import { LightMenuWithTopBannerAndAuthDialog as LightMenu } from '@crehana/web';

import Media from 'Shared/ui/Media';
import Container2 from 'Shared/ui/Container';

import { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';

import { useAmplitude, getSourceEventParams } from 'Jsx/Analytics/Amplitude';

import GET_BEST_SELLER_COURSES_QUERY from './graphql/getBestSellerCourses.graphql';
import GET_RECOMMENDED_COURSES_QUERY from './graphql/getRecommendedCourses.graphql';
import GET_RECENTLY_SEEN_COURSES_QUERY from './graphql/getRecentlySeenCourses.graphql';
import Summary from './components/Summary';
import ShoppingCartItems from './components/ShoppingCartItems';
import EmptyCart from './components/EmptyCart';

import StickyFooter from './components/StickyFooter';
import MobileBestSellerCourses from './components/BestSellerCourses/MobileBestSellerCourses';
import MobileRecentlySeenCourses from './components/RecentlySeenCourses/MobileRecentlySeenCourses';
import MobileRecommendedCourses from './components/RecommendedCourses/MobileRecommendedCourses';
import RecentlySeenCourses from './components/RecentlySeenCourses/RecentlySeenCourses';

import { TKeys } from './types';

const BestSellerCourses = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ShoppingCart-BestSellerCourses" */ './components/BestSellerCourses'
    ),
);

const RecommendedCourses = React.lazy(
  () =>
    import(
      /* webpackChunkName: "ShoppingCart-RecommendedCourses" */ './components/RecommendedCourses/RecommendedCourses'
    ),
);

const CustomLoader = ({ size = 56 }) => (
  <div className="flex justify-center w-full p-24">
    <Loader size={size as SpacingType} />
  </div>
);

export type ExtraState = {};

export type AppProps = {
  catalogUrl: string;
  accumulativeDiscount: boolean;
};

const App: React.FC<InitialStateResult<AppProps & ExtraState>> = props => {
  const { t } = useTranslation<TKeys>();

  const { me } = useMe();
  const [recentlySeenCourses, setRecentlySeenCourses] =
    React.useState<any[] | null>(null);
  const [bestSellerCourses, setBestSellerCourses] =
    React.useState<any[] | null>(null);
  const [recommendedCourses, setRecommendedCourses] =
    React.useState<any[] | null>(null);
  const { logEvent } = useAmplitude();

  const [modelNameUsedInRecommendations, setModelNameUsedInRecommendations] =
    React.useState('');

  React.useEffect(() => {
    logEvent({
      event: 'Render Shopping Cart Landing',
      data: {
        ...getSourceEventParams(),
      },
    });
  }, []);

  const { catalogUrl, productsContext, accumulativeDiscount } = props;
  const { shoppingCartState } = productsContext;
  const totalShoppingCartItems = shoppingCartState.items.length;
  const isCartEmpty = totalShoppingCartItems === 0;
  const {
    shoppingCartState: {
      loadingCart,
      loadingCartMutation,
      cartSummary,
      nextDiscount,
    },
  } = productsContext;
  const coursesLength = shoppingCartState.items.filter(
    item => item.type === 'COURSE',
  ).length;

  const getBestSellerCourses = () => {
    props.client
      .query({
        query: GET_BEST_SELLER_COURSES_QUERY,
        variables: { first: 9, bestSeller: 'week' },
        fetchPolicy: 'network-only',
      })
      .then(res => {
        if (!res.data.searchCourses) {
          throw Error('');
        }
        const coursesList = res.data.searchCourses.edges.map(el => el.node);

        setBestSellerCourses(coursesList);
      })
      .catch(err => {
        console.log('err', err.message);
      });
  };

  const fetchRecommendedCourses = () => {
    props.client
      .query({
        query: GET_RECOMMENDED_COURSES_QUERY,
        variables: { first: 8 },
        fetchPolicy: 'network-only',
      })
      .then(res => {
        if (!res.data.shoppingCartCoursesRecommendedByHana) {
          throw Error('');
        }
        const coursesList =
          res.data.shoppingCartCoursesRecommendedByHana.edges.map(
            el => el.node,
          );

        const { modelName } = res.data.shoppingCartCoursesRecommendedByHana;

        setModelNameUsedInRecommendations(modelName);
        setRecommendedCourses(coursesList);
      })
      .catch(err => {
        console.log('err', err.message);
      });
  };

  const getRecentlySeenCourses = () => {
    props.client
      .query({
        query: GET_RECENTLY_SEEN_COURSES_QUERY,
        variables: {
          first: 9,
        },
      })
      .then(res => {
        if (
          res.data &&
          res.data.coursesRecentlyViewed &&
          res.data.coursesRecentlyViewed.edges
        ) {
          setRecentlySeenCourses(
            res.data.coursesRecentlyViewed.edges.map(el => el.node),
          );
        } else {
          setRecentlySeenCourses([]);
        }
      });
  };

  React.useEffect(() => {
    getBestSellerCourses();
    fetchRecommendedCourses();
    getRecentlySeenCourses();
  }, []);

  React.useEffect(() => {
    fetchRecommendedCourses();
  }, [props.productsContext.shoppingCartState.items]);

  React.useEffect(() => {
    if (shoppingCartState?.courseIdsInCart?.length > 0) {
      // DATALAYER FOR GOOGLE ADS DYNAMIC REMARKETING
      const dynxItemId = shoppingCartState?.courseIdsInCart
        .map(courseId => courseId)
        .toString();
      const dynxItemId2 = shoppingCartState?.courseIdsInCart
        .map(courseId => `course_${courseId}`)
        .toString();
      const dynxTotalValue = shoppingCartState?.items.reduce(
        (accumulator, item) =>
          item.type === 'COURSE'
            ? accumulator + item.priceInDollars
            : accumulator,
        0,
      );

      (window.dataLayer || []).push({
        google_tag_params: {
          dynx_itemid: dynxItemId,
          dynx_itemid2: dynxItemId2,
          dynx_pagetype: 'conversionintent',
          dynx_totalvalue: dynxTotalValue,
        },
      });
    }
  }, [props.productsContext.shoppingCartState.cartSummary.totalPrice]);

  const renderMobileComponents = () => {
    if (!recentlySeenCourses || !bestSellerCourses) {
      return (
        <Container2>
          <span />
        </Container2>
      );
    }

    if (shoppingCartState.items.length === 0) {
      if (me) {
        if (recentlySeenCourses && recentlySeenCourses.length > 0) {
          return <MobileRecentlySeenCourses items={recentlySeenCourses} />;
        }
        return (
          <MobileBestSellerCourses
            items={bestSellerCourses}
            excludedCourseIds={shoppingCartState.courseIdsInCart}
          />
        );
      }
    }

    return (
      <MobileRecommendedCourses
        items={coursesLength > 0 ? recommendedCourses : bestSellerCourses}
        isRecommended={coursesLength > 0}
        excludedCourseIds={shoppingCartState.courseIdsInCart}
        modelNameUsedInRecommendations={modelNameUsedInRecommendations}
      />
    );
  };

  const renderDesktopComponents = () => {
    if (!recentlySeenCourses || !bestSellerCourses || !recommendedCourses)
      return <span />;

    const renderRecentlyViewed = () => {
      if (shoppingCartState.items.length === 0) {
        if (recentlySeenCourses.length > 0) {
          return <RecentlySeenCourses items={recentlySeenCourses} />;
        }
      }

      return <span />;
    };

    const renderBottomSection = () => {
      if (shoppingCartState.items.length === 0) {
        return <BestSellerCourses items={bestSellerCourses} />;
      }

      return (
        <RecommendedCourses
          items={recommendedCourses}
          modelNameUsedInRecommendations={modelNameUsedInRecommendations}
        />
      );
    };

    return (
      <React.Suspense fallback={<CustomLoader />}>
        {renderRecentlyViewed()}
        {renderBottomSection()}
      </React.Suspense>
    );
  };

  const hasNextDiscount =
    accumulativeDiscount && !!nextDiscount && coursesLength > 0;

  return (
    <div className={`${isCartEmpty ? 'pb-48' : 'pb-112'} sm:pb-0`}>
      <LightMenu
        layoutConfig={{
          hideShoppingCart: true,
        }}
      />
      <div>
        <div className="relative pt-40 md:pt-56">
          {/* Main section */}
          <Container>
            {!loadingCart && (
              <h1 className="mb-8 font-h4">
                {hasNextDiscount
                  ? t('ACCUMULATIVE_DISCOUNT')
                  : t('YOUR_SHOPPING_CART')}

                {hasNextDiscount && (
                  <span className="text-red-main"> {nextDiscount}% dto!</span>
                )}
              </h1>
            )}
          </Container>
          {loadingCart ? (
            <Container2>
              <CustomLoader />
            </Container2>
          ) : (
            <div className="mb-48">
              {isCartEmpty ? (
                <EmptyCart />
              ) : (
                <Container className="flex">
                  {/* Courses in shopping cart */}
                  <Col xs={12} md={7} lg={8}>
                    {/* Subtitle */}
                    <p className="mb-8 font-body3">{`${t('YOU_HAVE', {
                      defaultValue: 'Tienes',
                    })} ${totalShoppingCartItems} ${
                      totalShoppingCartItems > 1 ? 'ítems' : 'item'
                    } ${t('IN_YOUR_CART', {
                      defaultValue: 'en tu carrito',
                    })}`}</p>
                    <ShoppingCartItems items={shoppingCartState.items} />
                  </Col>
                  {/* Total payment summary */}
                  <Col xs={12} md={5} lg={4} className="hidden md:block">
                    <Summary
                      cartSummary={cartSummary}
                      loadingCart={loadingCart}
                    />
                  </Col>
                </Container>
              )}
            </div>
          )}
          <Media from="sm">
            {matches =>
              matches ? (
                renderDesktopComponents()
              ) : (
                <Container2>{renderMobileComponents()}</Container2>
              )
            }
          </Media>
          {!loadingCart && (
            <div className="flex items-center justify-center pb-24 mt-48 sm:hidden">
              <a
                className="mr-16 font-subtitle2 text-secondary-main"
                href={catalogUrl}
              >
                {t('KEEP_BUYING')}
              </a>
              <NewArrowRight size={12} color={colors.secondaryMain} />
            </div>
          )}
          {!isCartEmpty && (
            <StickyFooter
              loadingCart={loadingCart}
              loadingCartMutation={loadingCartMutation}
              cartSummary={cartSummary}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const ShoppingCart = props => {
  const context = useProductsContainer();

  return <App productsContext={context} {...props} />;
};

export default compose(withApollo)(ShoppingCart);
